import { useState, useCallback, memo, useMemo, useRef, useEffect, ReactNode } from 'react';
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { PlusCircle, Loader2, Filter } from 'lucide-react';
import { DrinksList } from './components/DrinksList';
import { AddSightingModal } from '@/components/sightings/AddSightingModal';
import { useAppSelector } from '@/store/hooks';
import {
    selectActiveLocationDetails,
    selectActiveLocationSightings,
    selectActiveLocationDetailsLoading,
    selectActiveLocationSightingsLoading,
} from '@/store/slices/activeLocationSlice';
import type { ReduxSighting } from '@/store/slices/activeLocationSlice';
import {
    selectAllBrands,
    selectAllFlavors,
    selectAllDrinks,
    selectGlobalDataInitialized
} from '@/store/slices/globalDataSlice';
import { selectUser, selectAuthLoading, selectAuthLoaded } from '@/store/slices/authSlice';
import {
    selectLocationsLoading,
} from '@/store/slices/locationsSlice';
import type { Flavor, Drink, DrinkFlavor } from '@/types/firestore';
import { toast } from 'sonner';
import { reportSighting, SightingPayload } from '@/lib/api';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";

const numberToDate = (timestamp: number | undefined): Date | undefined => {
    return timestamp ? new Date(timestamp) : undefined;
};

interface StandardLocationViewProps {
  renderComments?: () => ReactNode;
}

export const StandardLocationView = memo(({ renderComments }: StandardLocationViewProps) => {

  // --- RTK Selectors ---
  const locationData = useAppSelector(selectActiveLocationDetails);
  const sightings = useAppSelector(selectActiveLocationSightings);
  
  const brands = useAppSelector(selectAllBrands);
  const flavors = useAppSelector(selectAllFlavors);
  const allDrinks = useAppSelector(selectAllDrinks);
  const globalDataInitialized = useAppSelector(selectGlobalDataInitialized);

  const user = useAppSelector(selectUser);
  const authLoading = useAppSelector(selectAuthLoading);
  const authLoaded = useAppSelector(selectAuthLoaded);
  
  const locationsLoadingState = useAppSelector(selectLocationsLoading);
  const sightingsLoadingState = useAppSelector(selectActiveLocationSightingsLoading);
  const detailsLoadingState = useAppSelector(selectActiveLocationDetailsLoading);

  // --- Local UI State ---
  const [brandFilters, setBrandFilters] = useState<Record<string, boolean>>({});
  const [flavorFilters, setFlavorFilters] = useState<Record<string, boolean>>({});
  const [isConfirmingSightingId, setIsConfirmingSightingId] = useState<string | null>(null);
  const [isAddSightingOpen, setIsAddSightingOpen] = useState<boolean>(false);
  
  // --- Derived State & Memos ---
  const globalDataLoading = !globalDataInitialized;
  const visitedLoading = authLoading === 'pending' || !authLoaded;
  const locationsOperationLoading = locationsLoadingState === 'pending';

  const { drinks, drinksLoading } = useMemo(() => {
    
    if (!locationData?.id || !sightings || !allDrinks) {
      return { drinks: [], drinksLoading: true }; 
    }
    
    if (sightingsLoadingState === 'pending') {
      return { drinks: [], drinksLoading: true };
    }

    if (!globalDataInitialized) {
      return { drinks: [], drinksLoading: true };
    }
    
    if (sightings.length === 0 || allDrinks.length === 0) {
      return { drinks: [], drinksLoading: false };
    }
    
    const drinkMap = allDrinks.reduce((map: Map<string, Drink>, drink: Drink) => {
      if (drink.id) map.set(drink.id, drink);
      return map;
    }, new Map<string, Drink>());
    
    type ProcessedDrink = Drink & { 
      sightingId: string; 
      sightingData: ReduxSighting;
      brandName: string;
      drinkId: string;
    };

    const processedDrinks = sightings
      .map((sighting: ReduxSighting): ProcessedDrink | null => {
        const drinkDetails = sighting.drinkId ? drinkMap.get(sighting.drinkId) : null;
        if (!drinkDetails || !sighting.id || !sighting.drinkId) return null;
        return {
          ...drinkDetails,
          drinkId: sighting.drinkId,
          sightingId: sighting.id,
          sightingData: sighting,
          brandName: drinkDetails.brandName || 'Unknown Brand'
        };
      })
      .filter((drink): drink is ProcessedDrink => drink !== null);
    
    return { drinks: processedDrinks, drinksLoading: false };
  }, [locationData?.id, sightings, allDrinks, sightingsLoadingState, globalDataInitialized]);

  const locationBrandIds = useMemo(() => 
    [...new Set(drinks.map((d) => d?.brandId))].filter((id): id is string => !!id),
    [drinks]
  );

  const locationFlavors = useMemo(() => {
    if (drinks.length === 0 || !flavors?.length) return [];
    const drinkFlavorIds = new Set(drinks.flatMap((d) => d?.flavors?.map((f: DrinkFlavor) => f.id) ?? []));
    return flavors.filter((f: Flavor) => f.id && drinkFlavorIds.has(f.id));
  }, [drinks, flavors]);

  const prevDrinksRef = useRef<typeof drinks>([]);
  useEffect(() => {
    if (drinks === prevDrinksRef.current) return;

    const currentBrandIdsInDrinks = new Set(drinks.map(d => d?.brandId).filter((id): id is string => !!id));
    setBrandFilters(prevFilters => {
      const currentFilterKeys = new Set(Object.keys(prevFilters));
      const newFilters = { ...prevFilters };
      let needsUpdate = false;
      currentBrandIdsInDrinks.forEach(brandId => {
        if (!currentFilterKeys.has(brandId)) {
          newFilters[brandId] = false; needsUpdate = true;
        }
      });
      currentFilterKeys.forEach(brandId => {
         if (!currentBrandIdsInDrinks.has(brandId)) {
           delete newFilters[brandId]; needsUpdate = true;
         }
      });
      if (needsUpdate) {
        prevDrinksRef.current = drinks;
        return newFilters;
      }
      return prevFilters;
    });
  }, [drinks]); 

  const prevFlavorsRef = useRef<typeof flavors>([]);
  useEffect(() => {
    if (drinks === prevDrinksRef.current && flavors === prevFlavorsRef.current) return;

    const currentFlavorIdsInDrinks = new Set(drinks.flatMap(d => d?.flavors?.map(f => f.id) ?? []).filter((id): id is string => !!id));
    setFlavorFilters(prevFilters => {
      const currentFilterKeys = new Set(Object.keys(prevFilters));
      const newFilters = { ...prevFilters }; 
      let needsUpdate = false;
      currentFlavorIdsInDrinks.forEach(flavorId => {
        if (!currentFilterKeys.has(flavorId)) {
          newFilters[flavorId] = false; needsUpdate = true;
        }
      });
      currentFilterKeys.forEach(flavorId => {
         if (!currentFlavorIdsInDrinks.has(flavorId)) {
           delete newFilters[flavorId]; needsUpdate = true;
         }
      });
      if (needsUpdate) {
        prevDrinksRef.current = drinks;
        prevFlavorsRef.current = flavors;
        return newFilters;
      }
      return prevFilters;
    });
  }, [drinks, flavors]); 

  const filteredDrinks = useMemo(() => {
    return drinks.filter((drink) => { 
      if (!drink) return false;
      const brandFilterActive = Object.values(brandFilters).some(v => v);
      const flavorFilterActive = Object.values(flavorFilters).some(v => v);
      if (!brandFilterActive && !flavorFilterActive) return true;
      
      const matchesBrand = drink.brandId && (!brandFilterActive || brandFilters[drink.brandId]);
      if (flavorFilterActive && matchesBrand) {
        return drink.flavors?.some((flavor: DrinkFlavor) => flavor && flavor.id && flavorFilters[flavor.id]);
      }
      return matchesBrand;
    });
  }, [drinks, brandFilters, flavorFilters]);

  const filteredBrandIds = useMemo(() => {
    const flavorFilterActive = Object.values(flavorFilters).some(v => v);
    if (!flavorFilterActive) return locationBrandIds;
    
    return [...new Set(
      drinks
        .filter((drink) => drink?.flavors?.some((flavor: DrinkFlavor) => flavor && flavor.id && flavorFilters[flavor.id]))
        .map((drink) => drink?.brandId)
    )].filter((id): id is string => !!id);
  }, [locationBrandIds, drinks, flavorFilters]);

  const filteredFlavorIds = useMemo(() => {
    const brandFilterActive = Object.values(brandFilters).some(v => v);
    if (!brandFilterActive) return locationFlavors.map(f => f.id!).filter((id): id is string => !!id);
    
    const relevantDrinks = drinks.filter((drink) => drink?.brandId && brandFilters[drink.brandId]);
    return [...new Set(
      relevantDrinks.flatMap((drink) => drink?.flavors?.map((f: DrinkFlavor) => f.id) ?? [])
    )].filter((id): id is string => !!id);
  }, [locationFlavors, drinks, brandFilters]);

  const groupedDrinks = useMemo(() => {
    type GroupedDrinkDisplayForView = Drink & { 
        sightingId: string;
        sightingCount: number; 
        lastSighted?: Date;
        brandName: string;
        sightingData?: ReduxSighting;
    };

    return filteredDrinks.reduce((acc: Record<string, GroupedDrinkDisplayForView>, drink) => { 
      if (!drink?.id || !drink.brandId || !drink.sightingId || !drink.sightingData) return acc;
      
      const key = `${drink.brandId}-${drink.id}`;
      
      if (!acc[key]) {
        acc[key] = {
          ...drink,
          sightingId: drink.sightingId,
          brandName: drink.brandName || 'Unknown', 
          name: drink.name,
          sightingCount: drink.sightingData?.confirmationCount ?? 0,
          lastSighted: numberToDate(drink.sightingData?.lastConfirmed),
          sightingData: drink.sightingData,
        };
      } else {
        const currentLastSighted = acc[key].lastSighted;
        const newLastSighted = numberToDate(drink.sightingData?.lastConfirmed);

        if (newLastSighted && (!currentLastSighted || newLastSighted > currentLastSighted)) {
          acc[key].sightingId = drink.sightingId; 
          acc[key].sightingData = drink.sightingData;
          acc[key].lastSighted = newLastSighted;
        }
        acc[key].sightingCount = acc[key].sightingData?.confirmationCount ?? 0;
      }
      return acc;
    }, {} as Record<string, GroupedDrinkDisplayForView>); 
  }, [filteredDrinks]);

  const handleConfirmSightingCallback = useCallback(async (sightingDocumentId: string, drinkIdentityId: string) => {
    if (!locationData?.id || !user) {
        toast.error("Cannot confirm sighting: Missing location data or user not logged in.");
        return;
    }
    
    const confirmedDrinkInfo = drinks.find(d => d.sightingId === sightingDocumentId);
    const drinkDisplayName = confirmedDrinkInfo 
        ? `${confirmedDrinkInfo.brandName} ${confirmedDrinkInfo.name || ''}` 
        : 'Selected drink';
      
    setIsConfirmingSightingId(sightingDocumentId); 
    const loadingTimeout = setTimeout(() => setIsConfirmingSightingId(null), 10000);
      
    try { 
        await reportSighting({locationId: locationData.id, drinkId: drinkIdentityId} as SightingPayload);
        toast.success(`Confirmed availability for ${drinkDisplayName}!`);
    } catch(error: any) {
        toast.error(`Failed to confirm availability: ${error?.message || 'Unknown error'}`);
        console.error("Error confirming sighting:", error);
    } finally { 
        clearTimeout(loadingTimeout);
        setIsConfirmingSightingId(null); 
    }
  }, [user, locationData?.id, drinks]);

  const isOverallLoading = detailsLoadingState === 'pending' || visitedLoading || locationsOperationLoading;

  const handleAddSightingClick = useCallback(() => {
    setIsAddSightingOpen(true);
  }, []);

  const handleCloseAddSightingModal = useCallback(() => {
    setIsAddSightingOpen(false);
  }, []);

  const toggleBrandFilter = useCallback((brandId: string) => { 
      setBrandFilters(prev => ({ ...prev, [brandId]: !prev[brandId] }));
  }, []);
  
  const toggleFlavorFilter = useCallback((flavorId: string) => { 
      setFlavorFilters(prev => ({ ...prev, [flavorId]: !prev[flavorId] }));
  }, []);
  
  const setAllBrandFilters = useCallback((value: boolean) => { 
      setBrandFilters(prev => {
          const newFilters = { ...prev };
          filteredBrandIds.forEach(id => { if (id) newFilters[id] = value; }); 
          return newFilters;
      });
  }, [filteredBrandIds]);
  
  const setAllFlavorFilters = useCallback((value: boolean) => { 
       setFlavorFilters(prev => {
          const newFilters = { ...prev };
          filteredFlavorIds.forEach(id => { if (id) newFilters[id] = value; });
          return newFilters;
      });
  }, [filteredFlavorIds]);

  const areFiltersActive = useMemo(() => {
    return Object.values(brandFilters).some(v => v) || Object.values(flavorFilters).some(v => v);
  }, [brandFilters, flavorFilters]);

  if (detailsLoadingState === 'pending' && !locationData) {
      return (
          <div className="p-4 flex justify-center items-center min-h-[150px]">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              <span className="text-muted-foreground">Loading location details...</span>
          </div>
      );
  }
  
  if (!locationData) {
       return <div className="p-4 text-center text-destructive">Failed to load location details or location not found.</div>;
  }

  return (
    <>
      <div className="space-y-3">
        {renderComments && renderComments()}
        
        {renderComments && <Separator className="mt-3" />}
      </div>
      
      <div>
          <div className="flex justify-between items-center mb-1">
              <div className="flex items-center">
                  <Popover>
                      <PopoverTrigger asChild>
                          <Button 
                              variant={areFiltersActive ? "default" : "outline"} 
                              size="sm" 
                              className="h-7 w-7 p-0"
                          >
                              <Filter className="h-4 w-4" />
                          </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-72 bg-sidebar text-sidebar-foreground" side="top" align="end">
                          <div className="space-y-4">
                              <div>
                                  <div className="flex justify-between items-center mb-1.5">
                                      <h4 className="font-medium text-sm flex items-center">
                                          Brands {globalDataLoading && <Loader2 className="ml-1 h-3 w-3 animate-spin text-muted-foreground" />}
                                      </h4>
                                      <div className="flex space-x-1">
                                          <Button 
                                              variant="ghost" 
                                              size="sm"
                                              className="h-6 text-xs px-2"
                                              onClick={() => setAllBrandFilters(true)}
                                              disabled={filteredBrandIds.length === 0 || globalDataLoading}
                                          >
                                              All
                                          </Button>
                                          <Button 
                                              variant="ghost" 
                                              size="sm"
                                              className="h-6 text-xs px-2"
                                              onClick={() => setAllBrandFilters(false)}
                                              disabled={filteredBrandIds.length === 0 || globalDataLoading}
                                          >
                                              None
                                          </Button>
                                      </div>
                                  </div>
                                  <div className="border rounded p-2 h-24 overflow-y-auto relative">
                                      {globalDataLoading ? (
                                          <div className="flex justify-center items-center h-full">
                                              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                          </div>
                                      ) : locationBrandIds.length > 0 ? (
                                          locationBrandIds.map(brandId => {
                                              if (!brandId) return null;
                                              const brand = brands.find(b => b.id === brandId);
                                              if (!brand) return null;
                                              
                                              const isVisible = filteredBrandIds.includes(brandId);
                                              if (!isVisible) return null;
                                              
                                              return (
                                                  <div key={brandId} className="flex items-center space-x-2 mb-1.5">
                                                      <Checkbox 
                                                          id={`brand-${brandId}`}
                                                          checked={brandFilters[brandId] || false}
                                                          onCheckedChange={() => toggleBrandFilter(brandId)}
                                                      />
                                                      <label htmlFor={`brand-${brandId}`} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                                          {brand.name}
                                                      </label>
                                                  </div>
                                              );
                                          })
                                      ) : (
                                          <div className="text-sm text-muted-foreground text-center pt-4">No brands found</div>
                                      )}
                                  </div>
                              </div>
                              
                              <div>
                                  <div className="flex justify-between items-center mb-1.5">
                                      <h4 className="font-medium text-sm flex items-center">
                                          Flavors {globalDataLoading && <Loader2 className="ml-1 h-3 w-3 animate-spin text-muted-foreground" />}
                                      </h4>
                                      <div className="flex space-x-1">
                                          <Button 
                                              variant="ghost" 
                                              size="sm"
                                              className="h-6 text-xs px-2"
                                              onClick={() => setAllFlavorFilters(true)}
                                              disabled={filteredFlavorIds.length === 0 || globalDataLoading}
                                          >
                                              All
                                          </Button>
                                          <Button 
                                              variant="ghost" 
                                              size="sm"
                                              className="h-6 text-xs px-2"
                                              onClick={() => setAllFlavorFilters(false)}
                                              disabled={filteredFlavorIds.length === 0 || globalDataLoading}
                                          >
                                              None
                                          </Button>
                                      </div>
                                  </div>
                                  <div className="border rounded p-2 h-24 overflow-y-auto relative">
                                      {globalDataLoading ? (
                                          <div className="flex justify-center items-center h-full">
                                              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                          </div>
                                      ) : locationFlavors.length > 0 ? (
                                          locationFlavors.map(flavor => {
                                              if (!flavor.id) return null;
                                              const isVisible = filteredFlavorIds.includes(flavor.id);
                                              if (!isVisible) return null;
                                              
                                              return (
                                                  <div key={flavor.id} className="flex items-center space-x-2 mb-1.5">
                                                      <Checkbox 
                                                          id={`flavor-${flavor.id}`}
                                                          checked={flavorFilters[flavor.id] || false}
                                                          onCheckedChange={() => toggleFlavorFilter(flavor.id)}
                                                      />
                                                      <label htmlFor={`flavor-${flavor.id}`} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                                          {flavor.name}
                                                      </label>
                                                  </div>
                                              );
                                          })
                                      ) : (
                                          <div className="text-sm text-muted-foreground text-center pt-4">No flavors found</div>
                                      )}
                                  </div>
                              </div>
                          </div>
                      </PopoverContent>
                  </Popover>
                  <h4 className="font-medium text-sm ml-2">Previous sightings</h4>
              </div>
              <Button 
                  onClick={handleAddSightingClick} 
                  variant="outline" 
                  size="sm" 
                  className="h-7 text-xs"
                  disabled={isOverallLoading || !user}
              >
                  <PlusCircle className="w-3 h-3 mr-1" /> Add Sightings 
              </Button>
          </div>

          {locationData && locationData.id && (
            <DrinksList
              groupedDrinks={groupedDrinks}
              drinksLoading={drinksLoading}
              user={user}
              handleConfirmSighting={handleConfirmSightingCallback}
              isConfirmingSightingId={isConfirmingSightingId}
              locationId={locationData.id} 
            />
          )}
      </div>

      {user && locationData.id && (
        <AddSightingModal 
          isOpen={isAddSightingOpen}
          onClose={handleCloseAddSightingModal}
          locationId={locationData.id}
        />
      )}
    </>
  );
});

StandardLocationView.displayName = 'StandardLocationView'; 